<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
const logger = useLogger()
import type {
  IAoeCart,
  IAoeCartEarnedDiscount,
  IAoeCartItem,
} from '@/types/CartTypes.ts'
import { DeleteItemFromCart } from '@/services/CartService'
import AoeSingleLineEntry from './Controls/AoeSingleLineEntry.vue'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { SvgIcon } from '@progress/kendo-vue-common'
import { checkOutlineIcon, trashIcon } from '@progress/kendo-svg-icons'
import { SetIsLoading } from '@/services/AoeHelpers'
import { FormatCurrency } from '@/services/currencyFormatter'

const emit = defineEmits(['update-cart'])

const props = defineProps<{
  cart: IAoeCart
  earnedDiscount: IAoeCartEarnedDiscount
  GetCartItemsByPoeDiscPlanLevelDiscId(
    poeDiscPlanLevelDiscIdKey: number,
  ): IAoeCartItem[]
  FindCartItems(poeDiscPlanLevelDiscId: number): IAoeCartItem[]
  GetFullProductImageUrlFromStringDictionary(productCode: string): string
}>()

logger.debug('AoeCartDetailDiscountedSkuFromList props:', props)

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler', event)
  emit('update-cart', event)
}

function deleteCartItem(event: MouseEvent, lineNumber: number) {
  logger.debug('deleteCartItem', lineNumber)

  SetIsLoading(true)
  DeleteItemFromCart({
    aoeCartIdToken: props.cart ? props.cart.aoeCartIdToken : '',
    lineNumber,
  }).then(() => {
    logger.debug('DeleteItemFromCart success')
    SetIsLoading(false)
    emit('update-cart', event)
  })
}
</script>

<template>
  <div class="discounted-sku-from-list">
    <div
      v-if="
        props.FindCartItems(props.earnedDiscount.poeDiscPlanLevelDiscId)
          .length > 0
      "
    >
      <div
        v-for="(cartItem, index) in props.FindCartItems(
          props.earnedDiscount.poeDiscPlanLevelDiscId,
        )"
        :key="cartItem.lineNumber"
        class="row discounted-sku-from-list-row"
      >
        <div class="col-1 discounted-sku-from-list-chosen-check">
          <SvgIcon class="check-mark-outline-icon" :icon="checkOutlineIcon">
          </SvgIcon>
        </div>
        <div
          class="col-2 discountMessage discounted-sku-from-list-message"
          v-html="props.earnedDiscount.awardMessage"
        ></div>
        <div class="col-1 discounted-sku-from-list-chosen-image">
          <img
            :src="
              props.GetFullProductImageUrlFromStringDictionary(
                cartItem.productCode,
              )
            "
            class="img-fluid prodImage"
            :alt="t('cart.ProductImageAlt')"
          />
        </div>
        <div class="col-6 discounted-sku-from-list-chosen-name">
          {{ cartItem.skuName
          }}<span class="strike-through cart-item-retail-price">{{
            FormatCurrency(cartItem.price, null)
          }}</span
          ><span class="cart-item-price">{{
            FormatCurrency(
              cartItem.totalDiscountAmount
                ? cartItem.price - cartItem.totalDiscountAmount
                : cartItem.price,
              null,
            )
          }}</span>
        </div>
        <div class="col-1 discounted-sku-from-list-chosen-delete">
          <KButton
            :fill-mode="'clear'"
            @click="deleteCartItem($event, cartItem.lineNumber)"
            :svg-icon="trashIcon"
            :theme-color="'error'"
            class="percent-off-item-delete-icon"
          ></KButton>
        </div>
        <div
          v-if="
            index <
            props.FindCartItems(props.earnedDiscount.poeDiscPlanLevelDiscId)
              .length -
              1
          "
          class="row mt-3"
        >
          <div class="col-12 discount-divider unused-percent-off-item-divider">
            <hr />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="
        props.FindCartItems(earnedDiscount.poeDiscPlanLevelDiscId).length > 0 &&
        props.earnedDiscount.quantity > 0
      "
      class="row mt-3"
    >
      <div class="col-12 discount-divider unused-percent-off-item-divider">
        <hr />
      </div>
    </div>
    <div v-if="props.earnedDiscount.quantity > 0">
      <div
        v-for="x in props.earnedDiscount.quantity"
        :key="x"
        class="row discounted-sku-from-list-row"
      >
        <div
          class="col-md-4 col-sm-12 discountMessage"
          v-html="props.earnedDiscount.awardDescription"
        ></div>
        <div
          v-if="props.earnedDiscount.quantity > 0"
          class="col-md-8 col-sm-12 percent-off-item-entry-div"
        >
          <AoeSingleLineEntry
            :cart="props.cart"
            :showheader="false"
            :two-rows="true"
            :poediscplanleveldiscid="
              props.earnedDiscount.poeDiscPlanLevelDiscId
            "
            :skuList="props.earnedDiscount.items ?? []"
            @update-cart="updateCartHandler($event)"
          >
          </AoeSingleLineEntry>
        </div>
        <div v-if="x < props.earnedDiscount.quantity" class="row mt-3">
          <div class="col-12 discount-divider unused-percent-off-item-divider">
            <hr />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.discounted-sku-from-list-chosen-check,
.discounted-sku-from-list-message,
.discounted-sku-from-list-chosen-image,
.discounted-sku-from-list-chosen-name,
.discounted-sku-from-list-chosen-delete {
  align-content: center;
}

.discounted-sku-from-list-chosen-delete {
  text-align: right;
}

.strike-through {
  text-decoration: line-through;
}

.cart-item-retail-price,
.cart-item-price {
  margin-left: 20px;
}

.unused-percent-off-item-divider {
  margin-bottom: 1rem !important;
}
</style>
